// Dashboard de Atención Ciudadana — "Centro de Inteligencia Urbana".
// Mapa de calor (Leaflet) + KPIs + flujo de estados, categorías y tendencia.
// Genérico por org (el endpoint filtra por org_id del JWT). Lenguaje del SO:
// tarjetas Card del shell, KPIs con glifo de tono, barras propias (sin librería
// de gráficas) para que se lean igual en claro, oscuro y 390 px.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import {
  CheckCircle2, ChevronRight, Clock, Flame, History, Inbox, Layers, MapPin, MapPinned, RefreshCw, TrendingUp,
} from 'lucide-react';

import { Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { OsApp, osActionBtn } from '@/modules/orion-shell/OsApp';
import { getCitizenStats, getLegacyHeatmap, getPolygons, getReportByFolio } from '../api';
import type { CitizenReport, CitizenStats, LegacyHeatmap } from '../types';
import { TicketDetail } from './TicketDetail';
import { CitizenTabs } from './CitizenTabs';
import {
  CATEGORY_META, FLOW, FilterChip, Kpi, LayerToggle, STATUS_META, StatusChip, categoryColor, fmtN,
} from './parts';

// Basemap CARTO acorde al tema del dashboard (data-theme en <html>).
// Llave de CARTO Basemaps por despliegue (build arg VITE_CARTO_API_KEY, restringida al
// dominio de ese despliegue). Sin ella no se manda `key` (CARTO puede marcar las teselas).
const CARTO_API_KEY: string = ((import.meta.env.VITE_CARTO_API_KEY as string | undefined) || '').trim();
function tileUrlForTheme(): string {
  const theme = document.documentElement.getAttribute('data-theme');
  const style = theme === 'light' ? 'light_all' : 'dark_all';
  const base = `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png`;
  return CARTO_API_KEY ? `${base}?key=${encodeURIComponent(CARTO_API_KEY)}` : base;
}

// Recalcular tamaño SOLO si el contenedor mide (oculto/desmontado → 0 → heat revienta).
function safeInvalidate(map: L.Map | null) {
  const el = map?.getContainer();
  if (map && el && el.offsetWidth > 0 && el.offsetHeight > 0) map.invalidateSize();
}

// leaflet.heat pinta en un <canvas> del tamaño del mapa: si se agrega mientras el
// contenedor aún mide 0 (primer layout), revienta con getImageData. Espera a que mida.
function whenSized(map: L.Map, fn: () => void, tries = 30) {
  safeInvalidate(map);
  if (map.getSize().x > 0 && map.getSize().y > 0) { fn(); return; }
  if (tries > 0) requestAnimationFrame(() => whenSized(map, fn, tries - 1));
}

export function CitizenDashboard() {
  const [stats, setStats] = useState<CitizenStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ticket abierto desde "Reportes recientes" (se carga completo por folio).
  const [active, setActive] = useState<CitizenReport | null>(null);

  // Abrir un ticket desde cualquier parte del dashboard: trae el reporte
  // completo por su folio y muestra el detalle (conversación/evidencia/estado).
  const openFolio = (folio: string) => {
    getReportByFolio(folio).then(setActive).catch(() => { /* noop */ });
  };
  const [catFilter, setCatFilter] = useState<string>('');
  const [mapReady, setMapReady] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [legacy, setLegacy] = useState<LegacyHeatmap | null>(null);
  const [showPolygons, setShowPolygons] = useState(false);
  const [polygons, setPolygons] = useState<any | null>(null);
  const [showPins, setShowPins] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const heatRef = useRef<any>(null);
  const legacyHeatRef = useRef<any>(null);
  const polyRef = useRef<any>(null);
  const pinsRef = useRef<any>(null);
  const tileRef = useRef<any>(null);

  const load = () => {
    setLoading(true);
    getCitizenStats()
      .then((s) => { setStats(s); setError(null); })
      .catch((e) => setError(e?.message ?? 'Error cargando estadísticas'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Init mapa vía CALLBACK REF: se ejecuta cuando el <div> del mapa entra al
  // DOM (después del spinner de carga). Un useEffect con [] corría antes de
  // que el div existiera (estaba detrás del `if (loading)`) → mapa en blanco.
  const setMapNode = useCallback((node: HTMLDivElement | null) => {
    if (!node || mapRef.current) return;
    const map = L.map(node, { zoomControl: true, attributionControl: true })
      .setView([23.63, -102.55], 5); // vista neutral; se ajusta a los reportes de la org al cargar
    tileRef.current = L.tileLayer(tileUrlForTheme(), {
      attribution: '© OpenStreetMap · © CARTO', maxZoom: 19, subdomains: 'abcd',
    }).addTo(map);
    mapRef.current = map;
    setTimeout(() => safeInvalidate(map), 60);
    setMapReady(true);
  }, []);

  // Respeta el tema del dashboard (data-theme en <html>): tiles claros en
  // tema claro, oscuros en tema oscuro, y cambia al togglear el tema.
  useEffect(() => {
    const swap = () => tileRef.current?.setUrl(tileUrlForTheme());
    const obs = new MutationObserver(swap);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    swap();
    return () => obs.disconnect();
  }, [mapReady]);

  // Encuadre: la primera vez que llegan reportes, el mapa se ajusta a ELLOS (sin
  // coordenadas fijas de ninguna ciudad: sirve a cualquier org).
  const fittedRef = useRef(false);
  useEffect(() => {
    if (!mapRef.current || !stats || fittedRef.current) return;
    const ll = (stats.points || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)).map((p) => [p.lat, p.lng] as [number, number]);
    if (!ll.length) return;
    fittedRef.current = true;
    safeInvalidate(mapRef.current);
    mapRef.current.fitBounds(L.latLngBounds(ll).pad(0.08), { maxZoom: 14 });
  }, [stats, mapReady]);

  // Actualiza capa de calor cuando el mapa está listo y cambian stats/filtro.
  useEffect(() => {
    if (!mapRef.current || !stats) return;
    const pts = stats.points
      .filter((p) => (!catFilter || p.category === catFilter) && Number.isFinite(p.lat) && Number.isFinite(p.lng))
      .map((p) => [p.lat, p.lng, p.weight] as [number, number, number]);
    if (heatRef.current) { heatRef.current.remove(); heatRef.current = null; }
    const layer = (L as any).heatLayer(pts, {
      radius: 24, blur: 20, maxZoom: 14, minOpacity: 0.35,
      gradient: { 0.2: '#22d3ee', 0.45: '#4ade80', 0.65: '#facc15', 0.85: '#fb923c', 1.0: '#ef4444' },
    });
    heatRef.current = layer;
    const map = mapRef.current;
    whenSized(map, () => { if (heatRef.current === layer) layer.addTo(map); });
    setTimeout(() => safeInvalidate(mapRef.current), 100);
  }, [stats, catFilter, mapReady]);

  // Carga PEREZOSA del histórico legado (una sola vez, al activar el toggle).
  useEffect(() => {
    if (showLegacy && !legacy) {
      getLegacyHeatmap().then(setLegacy).catch(() => setShowLegacy(false));
    }
  }, [showLegacy, legacy]);

  // Capa de calor del LEGADO: gradiente VIOLETA (distinto de la capa viva) para
  // que se lean por separado. Densidad por colonia — los puntos comparten el
  // centroide de su colonia, así que la intensidad = # de reportes en la zona.
  useEffect(() => {
    if (!mapRef.current) return;
    if (legacyHeatRef.current) { legacyHeatRef.current.remove(); legacyHeatRef.current = null; }
    if (showLegacy && legacy) {
      const pts = legacy.points.map((p) => [p.lat, p.lng, 1] as [number, number, number]);
      const layer = (L as any).heatLayer(pts, {
        radius: 26, blur: 22, maxZoom: 14, minOpacity: 0.35,
        gradient: { 0.2: '#c4b5fd', 0.45: '#a78bfa', 0.65: '#8b5cf6', 0.85: '#7c3aed', 1.0: '#6d28d9' },
      });
      legacyHeatRef.current = layer;
      const map = mapRef.current;
      whenSized(map, () => { if (legacyHeatRef.current === layer) layer.addTo(map); });
      setTimeout(() => safeInvalidate(mapRef.current), 100);
    }
  }, [showLegacy, legacy, mapReady]);

  // Carga PEREZOSA de los polígonos del geofence (una vez, al activar).
  useEffect(() => {
    if (showPolygons && !polygons) {
      getPolygons().then(setPolygons).catch(() => setShowPolygons(false));
    }
  }, [showPolygons, polygons]);

  // Capa de POLÍGONOS del geofence (municipio/centro-historico/interapas): línea
  // por zona (relleno tenue) para leer las jurisdicciones sin tapar el calor. Color
  // por id. Fuente = ucognet (lo pintado = lo ruteado).
  useEffect(() => {
    if (!mapRef.current) return;
    if (polyRef.current) { polyRef.current.remove(); polyRef.current = null; }
    if (showPolygons && polygons && (polygons.features || []).length) {
      const gj = (L as any).geoJSON(polygons, {
        style: (f: any) => {
          const id = f?.properties?.id || '';
          const color = id === 'centro-historico' ? '#f59e0b' : id === 'interapas' ? '#0ea5e9' : '#22c55e';
          return { color, weight: 2, opacity: 0.9, fillColor: color, fillOpacity: 0.06 };
        },
        onEachFeature: (f: any, layer: any) => {
          const n = f?.properties?.nombre || f?.properties?.id || 'Zona';
          layer.bindTooltip(String(n), { sticky: true });
        },
      }).addTo(mapRef.current);
      // z-order: municipio (fondo) < interapas < centro-historico (frente) — para
      // que las zonas chicas no queden ocultas bajo las grandes.
      gj.eachLayer((layer: any) => { if (layer.feature?.properties?.id === 'interapas') layer.bringToFront(); });
      gj.eachLayer((layer: any) => { if (layer.feature?.properties?.id === 'centro-historico') layer.bringToFront(); });
      polyRef.current = gj;
      setTimeout(() => safeInvalidate(mapRef.current), 100);
    }
  }, [showPolygons, polygons, mapReady]);

  // 📍 PINES clicables sobre el calor: un marcador por punto, AGRUPADO por coordenada
  // (~11 m) para ver cuántos reportes caen en el MISMO lugar (detectar duplicados). El
  // popup lista los folios y al hacer clic abre el reporte. Respeta el filtro de categoría.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pinsRef.current) { pinsRef.current.remove(); pinsRef.current = null; }
    if (!showPins || !stats) return;
    const pts = (stats.points || []).filter((p) =>
      (!catFilter || p.category === catFilter) && p.folio && Number.isFinite(p.lat) && Number.isFinite(p.lng));
    const groups = new Map<string, any[]>();
    for (const p of pts) {
      const key = `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`;
      const g = groups.get(key);
      if (g) g.push(p); else groups.set(key, [p]);
    }
    const layer = (L as any).layerGroup();
    groups.forEach((arr) => {
      const { lat, lng } = arr[0];
      const n = arr.length;
      const color = n > 1 ? '#f59e0b' : '#3b82f6';
      const icon = (L as any).divIcon({
        className: '',
        html: `<div style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:${color};color:#fff;font:600 12px system-ui;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">${n > 1 ? n : '•'}</div>`,
        iconSize: [24, 24], iconAnchor: [12, 12],
      });
      const m = (L as any).marker([lat, lng], { icon });
      const box = document.createElement('div');
      box.style.cssText = 'min-width:190px;max-height:220px;overflow:auto';
      const head = document.createElement('div');
      head.style.cssText = 'font-weight:700;margin-bottom:6px';
      head.textContent = n > 1 ? `${n} reportes en este punto` : '1 reporte';
      box.appendChild(head);
      arr.forEach((r) => {
        const a = document.createElement('a');
        a.href = '#';
        a.style.cssText = 'display:block;padding:3px 0;color:#2563eb;text-decoration:none;font-size:12px;line-height:1.3';
        const lbl = CATEGORY_META[r.category]?.label ?? r.category;
        const st = STATUS_META[r.status]?.label ?? r.status;
        a.textContent = `${r.folio} · ${lbl} · ${st}`;
        a.onclick = (e) => { e.preventDefault(); openFolio(r.folio); map.closePopup(); };
        box.appendChild(a);
      });
      m.bindPopup(box);
      layer.addLayer(m);
    });
    layer.addTo(map);
    pinsRef.current = layer;
  }, [showPins, stats, catFilter, mapReady]);


  const catData = useMemo(
    () => [...(stats?.by_category ?? [])].sort((a, b) => b.count - a.count),
    [stats],
  );
  const statusTotal = useMemo(
    () => Object.values(stats?.by_status ?? {}).reduce((a, b) => a + b, 0),
    [stats],
  );
  const filtered = (stats?.points ?? []).filter((p) => !catFilter || p.category === catFilter);

  const frame = (content: ReactNode) => (
    <OsApp
      icon={MapPin}
      title="Atención Ciudadana"
      tabs={<CitizenTabs value="dashboard" />}
      subtitle="Centro de Inteligencia Urbana · reportes ciudadanos en tiempo real"
      actions={
        <button type="button" onClick={load} disabled={loading} className={osActionBtn}>
          <RefreshCw className={`h-4 w-4 ${loading && stats ? 'animate-spin' : ''}`} aria-hidden /> Actualizar
        </button>
      }
    >
      {content}
    </OsApp>
  );

  if (loading && !stats) return frame(<Loading label="Cargando inteligencia urbana…" />);
  if (error && !stats) return frame(<ErrorNote>{error}</ErrorNote>);
  if (!stats) return null;

  const resueltos = (stats.by_status.atendido ?? 0) + (stats.by_status.cerrado ?? 0);
  const maxCat = catData[0]?.count || 1;
  const maxCol = stats.top_colonias[0]?.count || 1;

  return frame(
    <div className="flex flex-col gap-4">
      {/* Un «Actualizar» fallido no esconde los datos, pero avisa que pueden estar viejos. */}
      {error && <ErrorNote>No se pudo actualizar: {error}. Se muestran los últimos datos cargados.</ErrorNote>}
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi icon={TrendingUp} tone="violet" label="Reportes · 30 días" value={fmtN(stats.last_30d)}
          sub={`${fmtN(stats.total)} en total`} />
        <Kpi icon={Inbox} tone="blue" label="Abiertos" value={fmtN(stats.open_count)}
          sub={stats.urgent_open > 0
            ? <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300">
                <Flame className="h-3 w-3" aria-hidden /> {fmtN(stats.urgent_open)} urgentes
              </span>
            : 'sin urgentes'} />
        <Kpi icon={CheckCircle2} tone="indigo" label="Resueltos" value={fmtN(resueltos)}
          sub={`${stats.resolved_pct}% del total`} />
        <Kpi icon={Clock} tone="magenta" label="Tiempo de resolución"
          value={stats.avg_resolution_hours != null ? `${stats.avg_resolution_hours} h` : '—'}
          sub="promedio, de recibido a atendido" />
      </div>

      {/* Mapa + flujo de estados */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Mapa de calor" bodyClassName="flex flex-col gap-3"
          action={<span className="text-xs tabular-nums text-muted-foreground">{fmtN(filtered.length)} puntos</span>}>
          {/* Filtro por categoría — solo las del ALCANCE del usuario: los stats vienen
              scopeados por buzón (scope_reports_qs), así que un director ve únicamente
              las de SU dependencia; un admin las ve todas. Excluye licencias (guía). */}
          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible">
            <FilterChip active={!catFilter} onClick={() => setCatFilter('')}>Todas</FilterChip>
            {(stats.by_category ?? [])
              .filter((c) => !c.category.startsWith('licencia_'))
              .map((c) => (
                <FilterChip key={c.category} active={catFilter === c.category} dot={categoryColor(c.category)}
                  onClick={() => setCatFilter(c.category)}>
                  {CATEGORY_META[c.category]?.label ?? c.label ?? c.category}
                </FilterChip>
              ))}
          </div>
          {/* Capas (no cuentan en los KPIs) */}
          <div role="group" aria-label="Capas del mapa" className="flex w-full items-center gap-1 overflow-x-auto rounded-xl bg-muted p-1 sm:w-auto sm:self-start">
            <LayerToggle on={showLegacy} onClick={() => setShowLegacy((v) => !v)} icon={History}
              title="Histórico del sistema anterior — capa separada, no cuenta en los KPIs">
              Histórico{showLegacy && legacy ? ` · ${fmtN(legacy.geocoded)} (${legacy.coverage_pct}%)` : ''}
            </LayerToggle>
            <LayerToggle on={showPolygons} onClick={() => setShowPolygons((v) => !v)} icon={Layers}
              title="Polígonos del geofence de la org (misma fuente que el ruteo)">
              Zonas{showPolygons && polygons ? ` · ${(polygons.features || []).length}` : ''}
            </LayerToggle>
            <LayerToggle on={showPins} onClick={() => setShowPins((v) => !v)} icon={MapPinned}
              title="Pines clicables por reporte, agrupados por punto: ve cuántos caen en el mismo lugar (duplicados) y abre cada uno">
              Pines{showPins ? ` · ${fmtN(filtered.filter((p) => p.folio).length)}` : ''}
            </LayerToggle>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border">
            <div ref={setMapNode} className="z-0 h-[320px] w-full sm:h-[420px]" />
          </div>
        </Card>

        <Card title="Flujo de los reportes" className="self-start"
          action={<span className="text-xs tabular-nums text-muted-foreground">{fmtN(statusTotal)}</span>}>
          {/* Barra apilada en el orden del flujo: recibido → … → cerrado */}
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" role="img"
            aria-label={FLOW.map((s) => `${STATUS_META[s].label}: ${stats.by_status[s] ?? 0}`).join(', ')}>
            {FLOW.map((s) => {
              const v = stats.by_status[s] ?? 0;
              return v > 0 ? <span key={s} style={{ width: `${(100 * v) / (statusTotal || 1)}%`, background: STATUS_META[s].color }} /> : null;
            })}
          </div>
          <ul className="mt-4 flex flex-col gap-1.5">
            {FLOW.map((s, i) => {
              const v = stats.by_status[s] ?? 0;
              return (
                <li key={s} className="flex items-center gap-3 rounded-xl bg-muted/50 px-3 py-2 text-sm">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold text-foreground"
                    style={{ background: `${STATUS_META[s].color}33` }}>{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-foreground">{STATUS_META[s].label}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">{statusTotal ? Math.round((100 * v) / statusTotal) : 0}%</span>
                  <span className="w-12 text-right font-semibold tabular-nums">{fmtN(v)}</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground">El flujo avanza; no regresa a fases anteriores.</p>
        </Card>
      </div>

      {/* Categorías + tendencia */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Reportes por categoría">
          {catData.length === 0 ? <EmptyState icon={<Inbox size={18} />} title="Sin reportes en el periodo" /> : (
            <ul className="flex flex-col gap-2.5">
              {catData.map((c) => (
                <li key={c.category}>
                  <button type="button" onClick={() => setCatFilter(catFilter === c.category ? '' : c.category)}
                    aria-pressed={catFilter === c.category}
                    className="group flex w-full flex-col gap-1 rounded-lg text-left">
                    <span className="flex w-full items-center gap-2 text-sm">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: categoryColor(c.category) }} aria-hidden />
                      <span className={`min-w-0 flex-1 truncate ${catFilter === c.category ? 'font-semibold text-foreground' : 'text-foreground group-hover:text-primary'}`}>
                        {CATEGORY_META[c.category]?.label ?? c.label ?? c.category}
                      </span>
                      <span className="font-semibold tabular-nums">{fmtN(c.count)}</span>
                    </span>
                    <span className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <span className="block h-full rounded-full" style={{ width: `${(100 * c.count) / maxCat}%`, background: categoryColor(c.category) }} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">Toca una categoría para filtrar el mapa.</p>
        </Card>

        <Card title="Tendencia · 30 días"
          action={<span className="text-xs tabular-nums text-muted-foreground">{fmtN(stats.trend.reduce((a, t) => a + t.count, 0))} reportes</span>}>
          <TrendBars data={stats.trend} />
        </Card>
      </div>

      {/* Top colonias + recientes */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Colonias con más reportes">
          {stats.top_colonias.length === 0 ? <EmptyState icon={<MapPin size={18} />} title="Sin colonias con reportes" /> : (
            <ol className="flex flex-col gap-1.5">
              {stats.top_colonias.map((c, i) => (
                <li key={c.colonia} className="flex items-center gap-3 rounded-xl bg-muted/50 px-3 py-2 text-sm">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-foreground">{c.colonia}</span>
                  <span className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-muted sm:block">
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${(100 * c.count) / maxCol}%` }} />
                  </span>
                  <span className="w-10 text-right font-semibold tabular-nums">{fmtN(c.count)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <Card title="Reportes recientes">
          {stats.recent.length === 0 ? <EmptyState icon={<Inbox size={18} />} title="Aún no hay reportes" /> : (
            <ul className="flex max-h-[340px] flex-col gap-1.5 overflow-y-auto pr-1">
              {stats.recent.map((r) => (
                <li key={r.folio}>
                  <button type="button" onClick={() => openFolio(r.folio)}
                    className="flex w-full items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-muted/40">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: categoryColor(r.category) }} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-foreground">{r.label}</span>
                        <span className="hidden shrink-0 font-mono text-[11px] text-muted-foreground sm:inline">{r.folio}</span>
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">{r.address}</span>
                    </span>
                    <StatusChip status={r.status} />
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {active && (
        <TicketDetail
          report={active}
          onClose={() => setActive(null)}
          onChanged={(u) => setActive(u)}
        />
      )}
    </div>,
  );
}

/** Columnas diarias (SVG propio): se leen igual en claro/oscuro y no dependen
 *  del tamaño del contenedor al montar. */
function TrendBars({ data }: { data: { date: string; count: number }[] }) {
  if (!data.length) return <EmptyState icon={<TrendingUp size={18} />} title="Sin datos de tendencia" />;
  const max = Math.max(...data.map((d) => d.count), 1);
  const fmtDia = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };
  const pico = data.reduce((a, b) => (b.count > a.count ? b : a), data[0]);
  return (
    <div>
      <div className="flex h-44 items-end gap-[3px]" role="img"
        aria-label={`Reportes por día del ${fmtDia(data[0].date)} al ${fmtDia(data[data.length - 1].date)}; máximo ${pico.count} el ${fmtDia(pico.date)}`}>
        {data.map((d) => (
          <span key={d.date} title={`${fmtDia(d.date)}: ${d.count}`}
            className="min-w-0 flex-1 rounded-t-[4px] bg-primary/70 transition-colors hover:bg-primary"
            style={{ height: `${Math.max(d.count ? 4 : 1, (100 * d.count) / max)}%`, opacity: d.count ? 1 : 0.35 }} />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[11px] tabular-nums text-muted-foreground">
        <span>{fmtDia(data[0].date)}</span>
        <span>Pico: {pico.count} · {fmtDia(pico.date)}</span>
        <span>{fmtDia(data[data.length - 1].date)}</span>
      </div>
    </div>
  );
}

export default CitizenDashboard;
